import { useState, useMemo } from 'react';

import PropTypes from 'prop-types';

import useEnv from '@zenbusiness/application-commons-env';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';
import { useCustomForm } from '#client/contexts/CustomFormContext';

import * as Styled from './BusinessNameResults.style';
import { BusinessNameResultsData } from './types';
import PoweredByVelo from '../PoweredByVelo/PoweredByVelo';
import { ReportPanel } from '../ReportLayout/ReportPanel';
import SelectionPanel from '../ReportLayout/SelectionPanel';
import SidePanel from '../ReportLayout/SidePanel';

/**
 * Business Name Results component
 * Displays generated business name suggestions
 * Shows name availability, trademark info, domain availability, and research links
 * Uses shared ReportPanel for consistent reporting UI
 *
 * @param {object} props - Component props
 * @param {object} props.data - Business name results data containing items array
 * @param {Function} [props.onComponentClick] - Callback for component actions
 * @component
 */
function BusinessNameResults({ data, onComponentClick }) {
  const { DEPLOY_ENV } = useEnv('head-first');
  const { isLoading, setLoading, resetForm, clearAnswers } = useCustomForm();

  // Support both 'name' and 'title' keys for flexibility
  const getDisplayTitle = (item) => item?.name || item?.title;

  // Memoize items to prevent useMemo dependency issues
  const items = useMemo(() => data?.items || [], [data?.items]);

  const [selectedItem, setSelectedItem] = useState(getDisplayTitle(items[0]));

  // Find the currently selected item data
  const selectedItemData = useMemo(
    () => items.find((item) => getDisplayTitle(item) === selectedItem),
    [items, selectedItem]
  );

  const handleItemSelect = (item) => {
    setSelectedItem(item);
  };

  const handleStartOver = async () => {
    if (isLoading) return;
    setLoading(true);
    if (onComponentClick) {
      await resetForm();
      await clearAnswers();
      await onComponentClick({ type: 'business_name_results_start_over' });
    }
    setLoading(false);
  };

  // Handle case where no items are provided
  if (items.length === 0) {
    return (
      <Styled.BusinessNameResultsRoot>
        <Box style={{ textAlign: 'center' }}>
          <Text>No business names found</Text>
          <Text>
            We weren't able to generate business names based on your responses. Please try again.
          </Text>
        </Box>
      </Styled.BusinessNameResultsRoot>
    );
  }

  return (
    <Styled.BusinessNameResultsRoot>
      <Styled.NamesHeader>
        <Text size="md" weight="medium">
          Name ideas for your brand
        </Text>
      </Styled.NamesHeader>
      <Styled.NamesContent>
        <SidePanel
          selectionPanel={
            <SelectionPanel
              clickstreamPrefix="business_name"
              generateMoreText="Generate more names"
              getDisplayTitle={(item) => item.name || item.title}
              getItemId={(item) => item.name || item.title}
              items={items}
              loading={isLoading}
              selectedItemId={selectedItem}
              startOverText="Start over"
              onItemSelect={handleItemSelect}
              onStartOver={handleStartOver}
            />
          }
        />

        <ReportPanel
          emptyMessage="Select a business name to view details"
          reportData={selectedItemData}
        />
      </Styled.NamesContent>
      <PoweredByVelo url={ROUTES.website(DEPLOY_ENV)} />
    </Styled.BusinessNameResultsRoot>
  );
}

BusinessNameResults.propTypes = {
  data: BusinessNameResultsData,
  onComponentClick: PropTypes.func
};

export default BusinessNameResults;
